/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable import/no-extraneous-dependencies */
import { tableAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    defineStyle,
} from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';

const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
    table: {
        fontVariantNumeric: 'lining-nums tabular-nums',
        borderCollapse: 'collapse',
        width: 'full',
    },
    th: {
        fontFamily: 'body',
        fontWeight: 'bold',
        letterSpacing: 'normal',
        textAlign: 'start',
        textTransform: 'none',
    },
    td: {
        textAlign: 'start',
    },
    caption: {
        mt: 4,
        fontFamily: 'body',
        textAlign: 'center',
        fontWeight: 'medium',
    },
});

const numericStyles = defineStyle({
    '&[data-is-numeric=true]': {
        textAlign: 'end',
    },
});

const variantSimple = definePartsStyle((props) => {
    const { colorScheme: c } = props;

    return {
        th: {
            color: mode('muted.500', 'muted.400')(props),
            borderBottom: '1px',
            borderColor: mode(`${c}.50`, `${c}.700`)(props),
            ...numericStyles,
        },
        td: {
            borderBottom: '1px',
            borderColor: mode(`${c}.50`, `${c}.700`)(props),
            ...numericStyles,
        },
        caption: {
            color: mode('muted.600', 'muted.100')(props),
        },
        tfoot: {
            tr: {
                '&:last-of-type': {
                    th: { borderBottomWidth: 0 },
                },
            },
        },
    };
});

const variantStripe = definePartsStyle((props) => {
    const { colorScheme: c } = props;

    return {
        th: {
            color: mode('muted.500', 'muted.400')(props),
            borderBottom: '1px',
            borderColor: mode(`${c}.50`, `${c}.700`)(props),
            ...numericStyles,
        },
        td: {
            borderBottom: '1px',
            borderColor: mode(`${c}.50`, `${c}.700`)(props),
            ...numericStyles,
        },
        caption: {
            color: mode('muted.600', 'muted.100')(props),
        },
        tbody: {
            tr: {
                '&:nth-of-type(odd)': {
                    'th, td': {
                        borderBottomWidth: '1px',
                        borderColor: mode(`${c}.50`, `${c}.700`)(props),
                    },
                    td: {
                        background: mode(`${c}.50`, `${c}.700`)(props),
                    },
                },
            },
        },
        tfoot: {
            tr: {
                '&:last-of-type': {
                    th: { borderBottomWidth: 0 },
                },
            },
        },
    };
});

const variants = {
    simple: variantSimple,
    striped: variantStripe,
    unstyled: defineStyle({}),
};

const sizes = {
    sm: definePartsStyle({
        th: {
            px: '1',
            py: '1',
            lineHeight: '4',
            fontSize: 'xs',
        },
        td: {
            px: '1',
            py: '2',
            fontSize: 'sm',
            lineHeight: '4',
        },
        caption: {
            px: '1',
            py: '2',
            fontSize: 'xs',
        },
    }),
    md: definePartsStyle({
        th: {
            px: '1',
            py: '3',
            lineHeight: '4',
            fontSize: 'sm',
        },
        td: {
            px: '1',
            py: '4',
            lineHeight: '5',
        },
        caption: {
            px: '1',
            py: '2',
            fontSize: 'sm',
        },
    }),
    lg: definePartsStyle({
        th: {
            px: '1',
            py: '4',
            lineHeight: '5',
            fontSize: 'sm',
        },
        td: {
            px: '1',
            py: '5',
            lineHeight: '6',
        },
        caption: {
            px: '1',
            py: '2',
            fontSize: 'md',
        },
    }),
};

export const tableTheme = defineMultiStyleConfig({
    baseStyle,
    variants,
    sizes,
    defaultProps: {
        variant: 'simple',
        size: 'md',
        colorScheme: 'muted',
    },
});
